import React from 'react';
import * as _ from 'lodash';
import { messageUtility } from '../../utilities/messageUtility';
import { FieldMasterItemsDropdown } from '../../components/dropdowns/single/MasterItems';
import Joi from 'joi-browser';
import moment from 'moment';
import {
  FormElement,
  FormListItem,
  FormListSubHeader,
} from '../../components/CommonFormComponents';
import { RadioButtonIcon } from '../../components/icons/RadioButtonIcon';
moment.locale('ja');

// 日付のフォーマット
const DATE_FORMAT = 'YYYY/MM/DD(ddd) HH:mm';

// その他の数字
export const OTHER_VALUE = '0';

export const MASTER_TYPE = {
  // 機械
  MACHINE: 1,
  // 揚重機
  CRANE: 2,
};

// 機械マスタの必須項目
const MACHINE_REQUIRED_COLUMNS = ['type', 'name', 'company_branch_id'];

// 揚重機マスタの必須項目
const CRANE_REQUIRED_COLUMNS = [
  'type',
  'name',
  'spec_form',
  'partner_company_name',
];

// CO2の項目
export const CO2_REQUIRED_COLUMNS = [
  // 燃料購入元
  'ownership',
  // CO2排出量の計算方法
  'co2_calculation_method',
  // 機械分類(中)
  'middle_machine_id',
  // 機械分類(小)
  'small_machine_id',
  // 仕様分類
  'spec_id',
  // 油種
  'machine_fuel_id',
  // その他機材名
  'other_name',
];

export const CO2_DROPDOWN_NAME_COLUMNS = [
  'middle_machine_name',
  'small_machine_name',
  'spec_name',
  'machine_fuel_name',
];

const CO2_SCHEMA = {
  ownership: Joi.number().required(),
  co2_calculation_method: Joi.number().required(),
  middle_machine_id: Joi.number()
    .empty(['', null])
    .required(),
  middle_machine_name: Joi.string().allow('', null),
  small_machine_id: Joi.number()
    .empty('')
    .required(),
  small_machine_name: Joi.string().allow('', null),
  spec_id: Joi.number()
    .empty('')
    .required(),
  spec_name: Joi.string().allow('', null),
  machine_fuel_id: Joi.number()
    .empty([''])
    .required(),
  machine_fuel_name: Joi.string().allow('', null),
  other_name: Joi.string().required(),
};

// デフォルトの燃料購入元は左側の"協力会社持込"の1
const DEFAULT_OWNERSHIP = 1;
const DEFAULT_CO2_CALCULATION_METHOD = 0;

// CO2項目用formValues
export const CO2_FORM_VALUES = {
  // 燃料購入元
  ownership: DEFAULT_OWNERSHIP,
  // CO2排出量の計算方法
  co2_calculation_method: DEFAULT_CO2_CALCULATION_METHOD,
  // 機械分類(中)
  middle_machine_id: null,
  middle_machine_name: '',
  // 機械分類(小)
  small_machine_id: null,
  small_machine_name: '',
  // 仕様分類
  spec_id: null,
  spec_name: '',
  // 油種
  machine_fuel_id: null,
  machine_fuel_name: '',
  // その他機材名
  other_name: '',
};

/**
 * 必須キーの配列を返す
 *
 * @param {MASTER_TYPE} type 機械 or 揚重機
 * @param {boolean} isCO2 CO2項目有無
 * @param {array} requiredColums
 * @returns
 */
export const getRequiredColumns = (type, isCO2, requiredColums = []) => {
  let result = [];
  if (type === MASTER_TYPE.MACHINE) {
    result = MACHINE_REQUIRED_COLUMNS;
  } else if (type === MASTER_TYPE.CRANE) {
    result = CRANE_REQUIRED_COLUMNS;
  }

  // CO2の項目がある時は結合
  if (isCO2) {
    result = result.concat(requiredColums);
  }

  return result;
};

/**
 * schemaの変換
 *
 * @param {*} origin 元のJoi schema
 * @param {array} columns
 * @returns
 */
export const convertSchema = (origin, columns) => {
  const schema = columns.reduce((accum, col) => {
    accum[col] = CO2_SCHEMA[col];
    return accum;
  }, {});
  // schema更新
  return origin.append(schema);
};

/**
 * 日付のフォーマット変換(登録日時, 更新日時)
 * dateが''で来るときはその対策もある
 *
 * @param {string} date 元の日付
 * @returns フォーマット後の日付
 */
export const convertDate = date => {
  return date ? moment(date, DATE_FORMAT).format(DATE_FORMAT) : '';
};

/**
 * Checks if the first date is after the second date.
 *
 * @param {string} date1 - The first date to compare.
 * @param {string} date2 - The second date to compare.
 * @returns {boolean} - Returns true if the first date is after the second date, otherwise false.
 */
export const isAfterDate = (date1, date2) => {
  return moment(date1, DATE_FORMAT).isAfter(date2, DATE_FORMAT);
}

/**
 * エラーメッセージの取得
 *
 * @param {*} errors
 * @param {*} column
 * @returns
 */
export const getErrorMessage = (errors, column) => {
  return errors[column] || null;
};

/**
 * 必須項目の表示コンポーネント
 *
 * @param {*} requiredInput
 * @returns
 */
export const RequiredCountComponent = ({ requiredInput }) => {
  // totalは今のキーをみて数える
  const total = requiredInput.columns.length;

  return (
    <div className='setting-header__end-item'>
      {`必須項目の入力数 ${requiredInput.count}/${total}`}
    </div>
  );
};

/**
 * 燃料購入元のラジオボタン
 *
 * @param {*} { index, name, label, value, onClick, checked }
 * @returns
 */
const RadioButton = ({ index, name, label, value, onClick, checked }) => {
  return (
    <div className='form-list-radio__item'>
      <label className='radio'>
        <input
          type='radio'
          name={name}
          onClick={() => {
            onClick(index, name, value);
          }}
          value={value}
          checked={checked}
        />
        <RadioButtonIcon className='icon--2x' />
        <span>{label}</span>
      </label>
    </div>
  );
};

/**
 * CO2のフォーム
 *
 * @export
 * @class CO2Form
 * @extends {React.Component}
 */
export class CO2Form extends React.Component {
  constructor(props) {
    super(props);

    // ラジオボタンのアイテム達
    this.RADIO_ITEMS = [
      { value: 1, name: '協力会社' },
      { value: 2, name: '元請' },
    ];

    this.CO2_CALCULATION_METHOD_ITEMS = [
      { value: 0, name: '稼働(予定)時間' },
      { value: 1, name: '給油量' },
    ];

    this.state = {
      selected: {},
    };

    this.onRadioChange = this.props.onRadioChange.bind(this);
    this.onChange = this.props.onChange.bind(this);
    this.onBlur = this.props.onBlur.bind(this);
  }

  /**
   *
   * @returns trueなら隠す
   * @memberof CO2Form
   */
  isHideItem(name) {
    switch (name) {
      case 'small_machine_id':
        return (
          String(this.props.formValues.middle_machine_id) === OTHER_VALUE ||
          (!this.props.formValues.small_machine_id &&
            !this.props.formValues.middle_machine_id)
        );
      case 'spec_id':
        return (
          String(this.props.formValues.small_machine_id) === OTHER_VALUE ||
          (!this.props.formValues.spec_id &&
            !this.props.formValues.small_machine_id)
        );
      case 'other_name':
        return !(
          String(this.props.formValues.middle_machine_id) === OTHER_VALUE ||
          String(this.props.formValues.small_machine_id) === OTHER_VALUE ||
          String(this.props.formValues.spec_id) === OTHER_VALUE
        );
      case 'machine_fuel_id':
        return !(
          this.props.isUseTansomiru &&
          (String(this.props.formValues.middle_machine_id) === OTHER_VALUE ||
            String(this.props.formValues.small_machine_id) === OTHER_VALUE ||
            (this.props.formValues.small_machine_id &&
              this.props.formValues.spec_id))
        );
      default:
        return false;
    }
  }

  render() {
    return (
      <React.Fragment>
        <FormListSubHeader
          title={messageUtility.getMessage('I-001-009')}
          info={messageUtility.getMessage('I-001-010')}
        />
        <FormListItem label='燃料購入元' required={true}>
          {/* 燃料購入元のラジオボタン */}
          <FormElement>
            <div className='form-list-radio'>
              {_.map(this.RADIO_ITEMS, (item, index) => {
                return (
                  <RadioButton
                    key={`radio-button-${index}`}
                    index={index}
                    name='ownership'
                    label={item.name}
                    value={item.value}
                    onClick={this.onRadioChange}
                    checked={this.props.formValues.ownership === item.value}
                  />
                );
              })}
            </div>
          </FormElement>
        </FormListItem>
        <FormListItem label='CO2排出量の計算方法' required={true}>
          <FormElement
            validate={getErrorMessage(
              this.props.errors,
              'co2_calculation_method'
            )}
          >
            <div className='form-list-radio'>
              {_.map(this.CO2_CALCULATION_METHOD_ITEMS, (item, index) => {
                return (
                  <RadioButton
                    key={`radio-button-co2-${index}`}
                    index={index}
                    name='co2_calculation_method'
                    label={item.name}
                    value={item.value}
                    onClick={this.onRadioChange}
                    checked={
                      this.props.formValues.co2_calculation_method ===
                      item.value
                    }
                  />
                );
              })}
            </div>
          </FormElement>
        </FormListItem>
        <FormListItem label='機械分類(中)' required={true}>
          <FieldMasterItemsDropdown
            masterType={this.props.masterType}
            name='middle_machine_id'
            value={this.props.formValues.middle_machine_id}
            valueName={this.props.formValues.middle_machine_name}
            errors={this.props.errors}
            onChange={this.onChange}
            onBlur={this.onBlur}
          />
        </FormListItem>
        {!this.isHideItem('small_machine_id') && (
          <FormListItem label='機械分類(小)' required={true}>
            <FieldMasterItemsDropdown
              masterType={this.props.masterType}
              // API用
              formValues={this.props.formValues}
              name='small_machine_id'
              value={this.props.formValues.small_machine_id}
              valueName={this.props.formValues.small_machine_name}
              errors={this.props.errors}
              onChange={this.onChange}
              onBlur={this.onBlur}
              resetKey={this.props.formValues.middle_machine_id}
            />
          </FormListItem>
        )}
        {!this.isHideItem('spec_id') && (
          <FormListItem label='仕様分類' required={true}>
            <FieldMasterItemsDropdown
              masterType={this.props.masterType}
              // API用
              formValues={this.props.formValues}
              name='spec_id'
              value={this.props.formValues.spec_id}
              valueName={this.props.formValues.spec_name}
              errors={this.props.errors}
              onChange={this.onChange}
              onBlur={this.onBlur}
              resetKey={this.props.formValues.small_machine_id}
            />
          </FormListItem>
        )}
        {!this.isHideItem('other_name') && (
          <FormListItem label='その他機材名' required={true}>
            <FormElement
              validate={getErrorMessage(this.props.errors, 'other_name')}
            >
              <input
                // 上のプルダウンで「その他」の時グレーアウトを外す
                type='text'
                maxLength={127}
                name='other_name'
                value={this.props.formValues.other_name || ''}
                onChange={this.onChange}
                onBlur={this.props.handleBlurInputText}
              />
            </FormElement>
          </FormListItem>
        )}
        {!this.isHideItem('machine_fuel_id') && (
          <FormListItem label='油種' required={true}>
            <FieldMasterItemsDropdown
              masterType={this.props.masterType}
              // API用
              formValues={this.props.formValues}
              name='machine_fuel_id'
              value={this.props.formValues.machine_fuel_id}
              valueName={this.props.formValues.machine_fuel_name}
              errors={this.props.errors}
              onChange={this.onChange}
              onBlur={this.onBlur}
              resetKey={this.props.formValues.spec_id}
            />
          </FormListItem>
        )}
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/common/MasterParts.jsx